import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { Box, Card, CardContent, Typography, Avatar, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { mainPageRootStyle } from '../styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadAnalyticsData } from './actions';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatNumber } from '../utils/numberUtils';
import { DateRangeFilter } from '../components/DateRangeFilter';
import HandleSelector from '../components/HandleSelector';
import { filterDataByDateRange } from '../utils/dateRangeUtils';
import SkeletonChart from '../components/SkeletonChart';
import SkeletonMetricCards from '../components/SkeletonMetricCards';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PercentIcon from '@mui/icons-material/Percent';
import InsightsIcon from '@mui/icons-material/Insights';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RepeatIcon from '@mui/icons-material/Repeat';
import { calculateCorrelation, interpretCorrelation, getCorrelationColor } from '../utils/correlationUtils';
import { calculateFollowBackStats } from './followBackUtils';

function AnalyticsPage() {
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const growth = useSelector(state => state.analytics.growth);
    const botActivity = useSelector(state => state.analytics.botActivity);
    const loading = useSelector(state => state.analytics.loading);
    const userId = useSelector(state => state.auth.user?.uid);
    const navigate = useNavigate();
    const [dateRange, setDateRange] = useState('3months');
    const [followBackStats, setFollowBackStats] = useState({});
    const [followBackLoading, setFollowBackLoading] = useState(false);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedHandle = queryParams.get('handle');

    useEffect(() => {
        document.title = 'Analytics - Sky Toolbox';
    }, []);

    // Auto-select first handle if none is selected
    useEffect(() => {
        if (!selectedHandle && handles && handles.length > 0) {
            navigate(`/analytics?handle=${handles[0].handle}`, { replace: true });
        }
    }, [selectedHandle, handles, navigate]);

    useEffect(() => {
        dispatch(loadAnalyticsData(userId, selectedHandle));
        
        // Load follow-back stats
        const loadFollowBackStats = async () => {
            if (userId && selectedHandle) {
                setFollowBackLoading(true);
                const stats = await calculateFollowBackStats(userId, selectedHandle);
                setFollowBackStats(stats);
                setFollowBackLoading(false);
            }
        };
        
        loadFollowBackStats();
    }, [dispatch, selectedHandle, userId]);

    const handleSelectedHandleChange = (event) => {
        navigate(`/analytics?handle=${event.target.value}`);
    };

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    // Combine growth and bot activity data by date
    const combinedData = useMemo(() => {
        // Group bot activity by day
        const activityByDay = new Map();
        
        botActivity.forEach(activity => {
            const timestamp = new Date(activity.timestamp);
            const dateKey = timestamp.toISOString().split('T')[0];
            
            if (!activityByDay.has(dateKey)) {
                activityByDay.set(dateKey, {
                    date: dateKey,
                    likes: 0,
                    follows: 0,
                    unfollows: 0,
                    reposts: 0,
                    botRuns: 0
                });
            }
            
            const dayData = activityByDay.get(dateKey);
            dayData.likes += activity.likes || 0;
            dayData.follows += activity.follows || 0;
            dayData.unfollows += activity.unfollows || 0;
            dayData.reposts += activity.reposts || 0;
            dayData.botRuns += 1;
        });

        // Combine with growth data
        const growthByDay = new Map();
        growth.forEach(g => {
            const dateKey = g.date;
            growthByDay.set(dateKey, g);
        });

        // Merge both datasets
        const allDates = new Set([...activityByDay.keys(), ...growthByDay.keys()]);
        const combined = [];

        allDates.forEach(dateKey => {
            const activity = activityByDay.get(dateKey) || { likes: 0, follows: 0, unfollows: 0, reposts: 0, botRuns: 0 };
            const growthData = growthByDay.get(dateKey);

            combined.push({
                date: new Date(dateKey),
                dateKey,
                // Bot actions
                likes: activity.likes,
                follows: activity.follows,
                unfollows: activity.unfollows,
                reposts: activity.reposts,
                botRuns: activity.botRuns,
                // Growth metrics
                followersCount: growthData?.stat?.followersCount || 0,
                newFollowsCount: growthData?.stat?.newFollowsCount || 0,
                newLikesCount: growthData?.stat?.newLikesCount || 0,
                newRepostsCount: growthData?.stat?.newRepostsCount || 0,
            });
        });

        combined.sort((a, b) => a.date - b.date);
        return combined;
    }, [growth, botActivity]);

    const filteredData = useMemo(() => {
        return filterDataByDateRange(combinedData, dateRange);
    }, [combinedData, dateRange]);

    // Calculate correlation metrics
    const correlationMetrics = useMemo(() => {
        if (filteredData.length === 0) {
            return {
                followsCorrelation: 0,
                likesCorrelation: 0,
                repostsCorrelation: 0,
                conversionRate: 0,
                avgFollowersPerRun: 0,
                totalBotFollows: 0,
                totalNewFollowers: 0,
                totalBotLikes: 0,
                totalBotReposts: 0
            };
        }

        // Extract arrays for correlation calculation
        const botFollows = filteredData.map(d => d.follows);
        const botLikes = filteredData.map(d => d.likes);
        const botReposts = filteredData.map(d => d.reposts);
        const newFollowers = filteredData.map(d => d.newFollowsCount);

        // Calculate correlations
        const followsCorrelation = calculateCorrelation(botFollows, newFollowers);
        const likesCorrelation = calculateCorrelation(botLikes, newFollowers);
        const repostsCorrelation = calculateCorrelation(botReposts, newFollowers);

        // Calculate totals
        const totalBotFollows = botFollows.reduce((sum, val) => sum + val, 0);
        const totalNewFollowers = newFollowers.reduce((sum, val) => sum + val, 0);
        const totalBotLikes = botLikes.reduce((sum, val) => sum + val, 0);
        const totalBotReposts = botReposts.reduce((sum, val) => sum + val, 0);
        const totalBotRuns = filteredData.reduce((sum, d) => sum + d.botRuns, 0);

        // Calculate conversion rate (new followers per 100 bot follows)
        const conversionRate = totalBotFollows > 0 ? (totalNewFollowers / totalBotFollows) * 100 : 0;

        // Average followers gained per bot run
        const avgFollowersPerRun = totalBotRuns > 0 ? totalNewFollowers / totalBotRuns : 0;

        return {
            followsCorrelation,
            likesCorrelation,
            repostsCorrelation,
            conversionRate,
            avgFollowersPerRun,
            totalBotFollows,
            totalNewFollowers,
            totalBotLikes,
            totalBotReposts,
            totalBotRuns
        };
    }, [filteredData]);

    const isLoading = loading || !selectedHandle;
    const hasData = filteredData && filteredData.length > 0;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {new Date(label).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                        })}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Typography 
                            key={index} 
                            variant="body2" 
                            sx={{ color: entry.color }}
                        >
                            {entry.name}: <strong>{formatNumber(entry.value)}</strong>
                        </Typography>
                    ))}
                </Box>
            );
        }
        return null;
    };

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Analytics' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    📊 Analytics Dashboard
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Analyze bot effectiveness and follower growth correlation
                </Typography>
            </Box>
            
            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <HandleSelector
                    value={selectedHandle}
                    onChange={handleSelectedHandleChange}
                    handles={handles}
                    ariaLabel="Select handle to view analytics"
                />
                <DateRangeFilter 
                    value={dateRange} 
                    onChange={handleDateRangeChange}
                    sx={{ mt: 0, mb: 0 }}
                />
            </Box>

            {isLoading ? (
                <SkeletonMetricCards count={4} />
            ) : (
                <>
                    {/* Correlation Metrics */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InsightsIcon /> Correlation Insights
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    elevation={0}
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                            <CompareArrowsIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                            {(correlationMetrics.followsCorrelation * 100).toFixed(1)}%
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                            Follows → Growth
                                        </Typography>
                                        <Chip 
                                            label={interpretCorrelation(correlationMetrics.followsCorrelation)} 
                                            size="small"
                                            sx={{ 
                                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    elevation={0}
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        color: 'white',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                            <PercentIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                            {correlationMetrics.conversionRate.toFixed(2)}%
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Conversion Rate
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Followers per 100 follows
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    elevation={0}
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                        color: 'white',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                            <TrendingUpIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                            {correlationMetrics.avgFollowersPerRun.toFixed(1)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Avg Followers/Run
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    elevation={0}
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: 'white',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                            <CompareArrowsIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                            {formatNumber(correlationMetrics.totalNewFollowers)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                            Total New Followers
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            from {formatNumber(correlationMetrics.totalBotFollows)} bot follows
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Additional Correlation Metrics */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            Action Correlation Scores
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Card elevation={1} sx={{ p: 2 }}>
                                    <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                                        <Avatar sx={{ bgcolor: getCorrelationColor(correlationMetrics.followsCorrelation) }}>
                                            <PersonAddIcon />
                                        </Avatar>
                                        <Box flexGrow={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Bot Follows → New Followers
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                                {(correlationMetrics.followsCorrelation * 100).toFixed(1)}% ({interpretCorrelation(correlationMetrics.followsCorrelation)})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total: {formatNumber(correlationMetrics.totalBotFollows)} follows sent
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card elevation={1} sx={{ p: 2 }}>
                                    <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                                        <Avatar sx={{ bgcolor: getCorrelationColor(correlationMetrics.likesCorrelation) }}>
                                            <ThumbUpIcon />
                                        </Avatar>
                                        <Box flexGrow={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Bot Likes → New Followers
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                                {(correlationMetrics.likesCorrelation * 100).toFixed(1)}% ({interpretCorrelation(correlationMetrics.likesCorrelation)})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total: {formatNumber(correlationMetrics.totalBotLikes)} likes given
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card elevation={1} sx={{ p: 2 }}>
                                    <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                                        <Avatar sx={{ bgcolor: getCorrelationColor(correlationMetrics.repostsCorrelation) }}>
                                            <RepeatIcon />
                                        </Avatar>
                                        <Box flexGrow={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Bot Reposts → New Followers
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                                {(correlationMetrics.repostsCorrelation * 100).toFixed(1)}% ({interpretCorrelation(correlationMetrics.repostsCorrelation)})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total: {formatNumber(correlationMetrics.totalBotReposts)} reposts
                                    </Typography>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </>
            )}

            {isLoading ? (
                <SkeletonChart count={1} />
            ) : !hasData ? (
                <EmptyState
                    title="No data available"
                    message="Select a handle and date range to view analytics"
                />
            ) : (
                <>
                    {/* Combined Chart: Bot Actions vs Follower Growth */}
                    <Card elevation={2} sx={{ mb: 4, p: 2 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, ml: 2 }}>
                            Bot Activity vs Follower Growth
                        </Typography>
                        <ResponsiveContainer width="100%" height={450}>
                            <ComposedChart
                                data={filteredData}
                                margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                            >
                                <defs>
                                    <linearGradient id="colorFollows" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorNewFollowers" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e2e8f0"
                                    strokeOpacity={0.5}
                                />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric' 
                                    })}
                                    stroke="#94a3b8"
                                    style={{ fontSize: '0.875rem' }}
                                />
                                <YAxis 
                                    yAxisId="left"
                                    stroke="#94a3b8"
                                    style={{ fontSize: '0.875rem' }}
                                    label={{ value: 'Bot Actions', angle: -90, position: 'insideLeft' }}
                                />
                                <YAxis 
                                    yAxisId="right"
                                    orientation="right"
                                    stroke="#94a3b8"
                                    style={{ fontSize: '0.875rem' }}
                                    label={{ value: 'New Followers', angle: 90, position: 'insideRight' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend 
                                    wrapperStyle={{ 
                                        paddingTop: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: 600
                                    }}
                                />
                                <Bar 
                                    yAxisId="left"
                                    dataKey="likes" 
                                    name="Bot Likes" 
                                    fill="#ef4444"
                                    fillOpacity={0.8}
                                    radius={[4, 4, 0, 0]}
                                    stackId="a"
                                />
                                <Bar 
                                    yAxisId="left"
                                    dataKey="follows" 
                                    name="Bot Follows" 
                                    fill="#10b981"
                                    fillOpacity={0.8}
                                    radius={[4, 4, 0, 0]}
                                    stackId="a"
                                />
                                <Bar 
                                    yAxisId="left"
                                    dataKey="reposts" 
                                    name="Bot Reposts" 
                                    fill="#3b82f6"
                                    fillOpacity={0.8}
                                    radius={[4, 4, 0, 0]}
                                    stackId="a"
                                />
                                <Line 
                                    yAxisId="right"
                                    type="monotone" 
                                    dataKey="newFollowsCount" 
                                    name="New Followers" 
                                    stroke="#f59e0b"
                                    strokeWidth={3}
                                    dot={{ r: 4, strokeWidth: 2, fill: '#ffffff' }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Card>

                    {/* Follow-Back Rate Analytics */}
                    {!followBackLoading && Object.keys(followBackStats).length > 0 && (
                        <Card elevation={2} sx={{ mt: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <TrendingUpIcon sx={{ color: 'white' }} />
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'white' }}>
                                        Follow-Back Rate Analytics
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 3 }}>
                                    Which bots are most effective at getting follow-backs (updated when UnfollowBot runs)
                                </Typography>
                                <TableContainer sx={{ bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 2 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Bot Type</strong></TableCell>
                                                <TableCell align="right"><strong>Total Follows</strong></TableCell>
                                                <TableCell align="right"><strong>Follow Backs</strong></TableCell>
                                                <TableCell align="right"><strong>Follow-Back Rate</strong></TableCell>
                                                <TableCell align="right"><strong>Avg. Time</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(followBackStats)
                                                .sort(([, a], [, b]) => b.followBackRate - a.followBackRate)
                                                .map(([botType, stats]) => {
                                                    let rateColor = '#dc2626';
                                                    if (stats.followBackRate >= 30) rateColor = '#10b981';
                                                    else if (stats.followBackRate >= 15) rateColor = '#f59e0b';
                                                    
                                                    return (
                                                        <TableRow key={botType} hover>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {botType === 'FeedsBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#3b82f6' }}>F</Avatar>}
                                                                    {botType === 'SearchBot' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#10b981' }}>S</Avatar>}
                                                                    {botType === 'Unknown' && <Avatar sx={{ width: 32, height: 32, bgcolor: '#6b7280' }}>?</Avatar>}
                                                                    <Typography variant="body2" fontWeight={500}>{botType}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right">{formatNumber(stats.totalFollows)}</TableCell>
                                                            <TableCell align="right">{formatNumber(stats.followBacks)}</TableCell>
                                                            <TableCell align="right">
                                                                <Chip 
                                                                    label={`${stats.followBackRate.toFixed(1)}%`}
                                                                    size="small"
                                                                    sx={{ bgcolor: rateColor, color: 'white', fontWeight: 600 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {stats.avgTimeToFollowBack > 0 
                                                                    ? `${stats.avgTimeToFollowBack.toFixed(1)}d`
                                                                    : 'N/A'
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </Paper>
    );
}

export default AnalyticsPage;

